// Chat prefill for high-intent links into /create. "Try it" on TQA cards and
// "Start Building" on the Build an automation module append chat_prefill_*
// params to the /create URL; this module parses them and builds the prompt
// that seeds the chat intro input.

import truncate from "lodash/truncate"

export type TqaPrefillType = "trigger" | "query" | "action"

// A single TQA from a Try it card (sc-146433).
export interface TqaPrefill {
  kind: "try_it"
  type: TqaPrefillType
  tqaName: string
  serviceName: string
  activeTab: string | null
  serviceModuleName: string | null
  prompt: string
}

export type BuildAutomationSelectionChanged = "none" | "trigger" | "action" | "both"

// A trigger + action pair from the Build an automation module.
export interface BuildAutomationPrefill {
  kind: "build_automation"
  triggerName: string
  triggerServiceName: string
  actionName: string
  actionServiceName: string
  triggerServiceModuleName: string | null
  actionServiceModuleName: string | null
  selectionChanged: BuildAutomationSelectionChanged
  prompt: string
}

export type ChatPrefill = TqaPrefill | BuildAutomationPrefill

// The name cap keeps the longest prompt (the two-service template, ≈ 105 fixed
// chars plus four names) under the intro textarea's maxLength=500, which would
// otherwise cut it silently. 4 × 90 + 105 = 465.
const MAX_NAME_LENGTH = 90

// The textarea's maxLength counts UTF-16 code units, while lodash's truncate
// counts Unicode symbols — astral characters (emoji) are two units each, so
// names full of them can still overshoot. capPrompt is the final guard.
const MAX_PROMPT_CODE_UNITS = 500

const TEMPLATE_PARAM_BY_TYPE: Record<TqaPrefillType, string> = {
  trigger: "template_trigger",
  query: "template_queries",
  action: "template_actions",
}

const truncateName = (value: string) => truncate(value, { length: MAX_NAME_LENGTH, omission: "…" })

// UTF-16 encodes astral characters (e.g. emoji) as a high surrogate followed by
// a low surrogate; a string must never end between the two halves.
const HIGH_SURROGATE_START = 0xd800
const HIGH_SURROGATE_END = 0xdbff

const capPrompt = (prompt: string): string => {
  if (prompt.length <= MAX_PROMPT_CODE_UNITS) return prompt
  let cut = prompt.slice(0, MAX_PROMPT_CODE_UNITS - 1)
  const last = cut.charCodeAt(cut.length - 1)
  if (last >= HIGH_SURROGATE_START && last <= HIGH_SURROGATE_END) cut = cut.slice(0, -1)
  return `${cut}…`
}

const isTqaPrefillType = (value: string | null): value is TqaPrefillType =>
  value === "trigger" || value === "query" || value === "action"

const isSelectionChanged = (value: string | null): value is BuildAutomationSelectionChanged =>
  value === "none" || value === "trigger" || value === "action" || value === "both"

// e.g. template_trigger=weather.temperature_drops -> "weather".
const moduleFromTemplateParam = (urlParams: URLSearchParams, param: string): string | null =>
  urlParams.get(param)?.split(",")[0]?.split(".")[0]?.trim() || null

export const buildPrefillPrompt = ({
  type,
  tqaName,
  serviceName,
}: Pick<TqaPrefill, "type" | "tqaName" | "serviceName">): string =>
  capPrompt(
    `I want to build an Applet that uses the "${truncateName(tqaName)}" ${type} from the ${truncateName(serviceName)} service.`,
  )

// The same-service pairing names the service once.
export const buildBuildAutomationPrompt = ({
  triggerName,
  triggerServiceName,
  actionName,
  actionServiceName,
}: Pick<BuildAutomationPrefill, "triggerName" | "triggerServiceName" | "actionName" | "actionServiceName">): string => {
  const trigger = `"${truncateName(triggerName)}" trigger`
  const action = `"${truncateName(actionName)}" action`
  if (triggerServiceName === actionServiceName) {
    return capPrompt(
      `I want to build an Applet that uses the ${trigger} and the ${action} from the ${truncateName(triggerServiceName)} service.`,
    )
  }
  return capPrompt(
    `I want to build an Applet that uses the ${trigger} from the ${truncateName(triggerServiceName)} service and the ${action} from the ${truncateName(actionServiceName)} service.`,
  )
}

export const parseChatPrefillFromParams = (urlParams: URLSearchParams): TqaPrefill | null => {
  const type = urlParams.get("chat_prefill_type")
  if (!isTqaPrefillType(type)) return null

  const tqaName = urlParams.get("chat_prefill_tqa")?.trim()
  const serviceName = urlParams.get("chat_prefill_service")?.trim()
  if (!tqaName || !serviceName) return null

  return {
    kind: "try_it",
    type,
    tqaName,
    serviceName,
    activeTab: urlParams.get("chat_prefill_tab"),
    serviceModuleName: moduleFromTemplateParam(urlParams, TEMPLATE_PARAM_BY_TYPE[type]),
    prompt: buildPrefillPrompt({ type, tqaName, serviceName }),
  }
}

export const parseBuildAutomationPrefillFromParams = (urlParams: URLSearchParams): BuildAutomationPrefill | null => {
  const triggerName = urlParams.get("chat_prefill_trigger")?.trim()
  const triggerServiceName = urlParams.get("chat_prefill_trigger_service")?.trim()
  const actionName = urlParams.get("chat_prefill_action")?.trim()
  const actionServiceName = urlParams.get("chat_prefill_action_service")?.trim()
  if (!triggerName || !triggerServiceName || !actionName || !actionServiceName) return null

  const changed = urlParams.get("chat_prefill_changed")

  return {
    kind: "build_automation",
    triggerName,
    triggerServiceName,
    actionName,
    actionServiceName,
    triggerServiceModuleName: moduleFromTemplateParam(urlParams, "template_trigger"),
    actionServiceModuleName: moduleFromTemplateParam(urlParams, "template_actions"),
    selectionChanged: isSelectionChanged(changed) ? changed : "none",
    prompt: buildBuildAutomationPrompt({ triggerName, triggerServiceName, actionName, actionServiceName }),
  }
}

// The two link sources never share a URL, so the order is only a deterministic
// tie-break: a single-TQA prefill wins when both somehow appear.
export const parseAnyChatPrefillFromParams = (urlParams: URLSearchParams): ChatPrefill | null =>
  parseChatPrefillFromParams(urlParams) ?? parseBuildAutomationPrefillFromParams(urlParams)
